import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { cartMutation } from '@theme/cart-mutation';
let busy = false;
let scheduled = false;
let automaticFailed = false; // blocks automatic enrolment/normalisation after a failure
// SP fix (2026-10-18): the "no merchandise left" removal gets ONE retry, and only
// after cartMutation.recover() has re-read /cart.js and re-rendered the sections.
let removalFailures = 0;
const MAX_REMOVAL_ATTEMPTS = 2;
const preferenceKey = 'sp_shipping_protection';
const root = () => window.Shopify?.routes?.root || '/';

// Only a fresh, nonempty cart is enrolled automatically. An explicit off choice,
// or removal of an already-initialized service line, must not be overwritten.
function getDesiredUpdate({ merchandise, quantity, preference }) {
  if (!merchandise && quantity > 0) return { quantity: 0, preference: '' };
  if (merchandise && quantity > 1) return { quantity: 1, preference: 'on' };
  if (merchandise && quantity === 0 && !preference) return { quantity: 1, preference: 'on' };
  return null;
}
function reconcile() {
  scheduled = false;
  if (busy || cartMutation.busy) return;
  const wrapper = document.querySelector('.sp-cart-protection');
  if (!wrapper) return;
  // UAE gate (2026-10-18): protection is offered only to UAE shoppers paying in AED.
  // Elsewhere the server renders a hidden force-remove wrapper only when a leftover
  // protection line exists; it is removed (one safe retry), never added.
  if (wrapper.hasAttribute('data-sp-force-remove')) {
    if (Number(wrapper.dataset.protectionQuantity || 0) <= 0) return;
    if (removalFailures >= MAX_REMOVAL_ATTEMPTS) {
      const message = 'We could not remove Shipping Protection from your bag. Please reload the page.';
      const status = document.querySelector('[data-sp-cart-status]');
      if (!status || status.hidden || status.textContent !== message) cartMutation.notice(message);
      return;
    }
    // Keep an explicit opt-out; otherwise clear the preference.
    setProtection(wrapper, 0, wrapper.dataset.preference === 'off' ? 'off' : '', false, true);
    return;
  }
  const change = getDesiredUpdate({
    merchandise: wrapper.dataset.hasMerchandise === 'true',
    quantity: Number(wrapper.dataset.protectionQuantity || 0),
    preference: wrapper.dataset.preference || '',
  });
  if (!change) { if (wrapper.dataset.hasMerchandise === 'true') removalFailures = 0; return; }
  // The /cart page wrapper (sections/main-cart.liquid) only removes an orphaned fee line;
  // enrolment stays a drawer decision where the customer can see and switch it off.
  if (wrapper.hasAttribute('data-sp-removal-only') && change.quantity !== 0) return;
  const isOrphanRemoval = change.quantity === 0 && wrapper.dataset.hasMerchandise !== 'true';
  if (isOrphanRemoval) {
    if (removalFailures >= MAX_REMOVAL_ATTEMPTS) {
      const message = 'We could not remove Shipping Protection from your empty bag. Please reload the page.';
      const status = document.querySelector('[data-sp-cart-status]');
      // Guard: reconcile() runs from a MutationObserver, so only write when the text changes.
      if (!status || status.hidden || status.textContent !== message) cartMutation.notice(message);
      return; // checkout stays locked: cart-mutation blocks fee-only carts
    }
  } else if (automaticFailed) return;
  setProtection(wrapper, change.quantity, change.preference, false, isOrphanRemoval);
}
function scheduleReconcile() {
  if (scheduled) return;
  scheduled = true;
  // Native cart events can precede their own section morph. Reconcile afterward.
  setTimeout(reconcile, 0);
}
async function setProtection(wrapper, quantity, preference, restoreFocus = true, orphanRemoval = false) {
  if (busy) return;
  const token = cartMutation.begin();
  if (!token) {
    const input = wrapper.querySelector('input');
    if (input) input.checked = Number(wrapper.dataset.protectionQuantity || 0) > 0;
    return;
  }
  busy = true;
  const drawer = wrapper.closest('cart-drawer-component');
  const wasOpen = drawer?.querySelector('dialog.cart-drawer__dialog')?.open;
  // SP quick-add fix (2026-10-18): if the shopper closes the drawer while this request is in flight, do not
  // pop it open again afterwards or pull focus back into it (that reopen left a stale drawer over the page).
  let userClosed = false;
  const onUserClose = () => { userClosed = true; };
  drawer?.addEventListener('dialog:close', onUserClose);
  try {
    const cart = await cartMutation.json(`${root()}cart/update.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [wrapper.dataset.variantId]: quantity }, attributes: { [preferenceKey]: preference }, sections: token.ids, sections_url: window.location.pathname }),
    });
    // cart:update is also the drawer's auto-open event: never let this background update pop the drawer open
    // when it was closed when we started, or the shopper closed it while the request was in flight.
    await cartMutation.commit(token, cart, 'sp-cart-protection', false, { deferDrawerOpen: userClosed || !wasOpen });
    if (orphanRemoval) removalFailures = 0;
    const liveDrawer = document.querySelector('cart-drawer-component');
    if (wasOpen && !userClosed && !liveDrawer?.querySelector('dialog.cart-drawer__dialog')?.open) liveDrawer?.showDialog();
    if (restoreFocus && !userClosed) requestAnimationFrame(() => document.querySelector('.sp-cart-protection__input')?.focus({ preventScroll: true }));
  } catch {
    if (orphanRemoval) removalFailures += 1;
    else automaticFailed = true;
    // No immediate second POST: first reconcile preference, eligibility, controls AND totals
    // from /cart.js. reconcile() then retries an orphan removal at most once, on fresh data.
    await cartMutation.recover(token);
  } finally {
    drawer?.removeEventListener('dialog:close', onUserClose);
    busy = false;
    await cartMutation.end(token);
    scheduleReconcile();
  }
}
document.addEventListener('change', event => {
  if (!event.target.matches('.sp-cart-protection__input')) return;
  automaticFailed = false;
  removalFailures = 0;
  setProtection(event.target.closest('.sp-cart-protection'), event.target.checked ? 1 : 0, event.target.checked ? 'on' : 'off');
});
document.addEventListener(ThemeEvents.cartUpdate, event => {
  if (event.detail?.data?.source !== 'sp-cart-protection') scheduleReconcile();
});
document.addEventListener('sp:cart-idle', scheduleReconcile);
function initializeProtection() {
  new MutationObserver(scheduleReconcile).observe(document.body, { childList: true, subtree: true });
  scheduleReconcile();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeProtection, { once: true });
else initializeProtection();

// Reward presentation only; no cart mutation, voucher issuance or third-party code.
(() => {
  if (window.spCartRewardCelebrations) return;
  window.spCartRewardCelebrations = true;
  let previous = null;
  let queued = [];
  let scheduled = false;
  function crossed(before, after, thresholds) {
    return thresholds.filter(t => before < t && after >= t);
  }
  function celebrate(card, threshold, delay) {
    setTimeout(() => {
      if (!card.isConnected || !card.closest('dialog[open]')) return;
      card.dispatchEvent(new CustomEvent('sp:reward-celebration', {bubbles:true, detail:{threshold}}));
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const marker = card.querySelector(`.sp-cart-rewards__track [data-threshold="${threshold}"]`);
      if (!marker) return;
      const rect = marker.getBoundingClientRect();
      const layer = document.createElement('div');
      layer.className = 'sp-cart-confetti'; layer.setAttribute('aria-hidden','true');
      card.closest('dialog').append(layer);
      const colors = ['#ff1493','#ff8bc7','#ffc3e3','#70ddeb','#ffffff'];
      for (let i=0; i<28; i++) {
        const bit = document.createElement('i');
        bit.style.cssText = `left:${rect.left+rect.width/2}px;top:${rect.top+rect.height/2}px;background:${colors[i%colors.length]}`;
        layer.append(bit);
        const x = (Math.random()-.5)*240;
        bit.animate([
          {transform:'translate(0,0) rotate(0deg)',opacity:1},
          {transform:`translate(${x*.6}px,-${30+Math.random()*55}px) rotate(150deg)`,opacity:1,offset:.3},
          {transform:`translate(${x}px,${100+Math.random()*90}px) rotate(${300+Math.random()*360}deg)`,opacity:0}
        ], {duration:1000+Math.random()*400, easing:'cubic-bezier(.2,.6,.4,1)',fill:'forwards'});
      }
      setTimeout(()=>layer.remove(),1600);
    },delay);
  }
  function scan() {
    scheduled = false;
    const card = document.querySelector('.sp-cart-rewards');
    if (!card) { previous = 0; queued = []; return; }
    const total = Number(card.dataset.eligibleTotal);
    const thresholds = [...card.querySelectorAll('.sp-cart-rewards__track [data-threshold]')].map(e=>Number(e.dataset.threshold));
    if (previous !== null) queued.push(...crossed(previous,total,thresholds));
    previous = total;
    queued = [...new Set(queued)].filter(t=>total>=t);
    if (card.closest('dialog[open]') && queued.length) {
      queued.forEach((t,i)=>celebrate(card,t,i*400));
      queued = [];
    }
  }
  function schedule() { if (!scheduled) {scheduled=true; requestAnimationFrame(scan);} }
  function init() {
    scan();
    new MutationObserver(records=>{
      if (records.some(r=>r.type==='attributes' || [...r.addedNodes,...r.removedNodes].some(n=>n.nodeType===1 && !n.matches?.('.sp-cart-confetti, .sp-cart-confetti *')))) schedule();
    }).observe(document.documentElement,{childList:true,subtree:true,attributes:true,attributeFilter:['data-eligible-total','open']});
  }
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
})();

// Circular stepper; the existing stock-limited select remains the source of truth.
if (!customElements.get('sp-cart-quantity')) {
  customElements.define('sp-cart-quantity', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.cartObserver?.disconnect(); this.selectObserver?.disconnect();
      this.abort = new AbortController(); this.select = null;
      this.enhance();
      this.observer = new MutationObserver(() => this.enhance());
      this.observer.observe(this, { childList: true, subtree: true });
      this.cartObserver = new MutationObserver(() => this.sync());
      const cart = this.closest('cart-items-component');
      if (cart) this.cartObserver.observe(cart, { attributes: true, attributeFilter: ['class'] });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.cartObserver?.disconnect(); this.selectObserver?.disconnect();
    }
    options() { return [...this.select.options].filter(o => !o.disabled && !o.closest('optgroup:disabled')); }
    sync() {
      if (!this.select || !this.value || !this.minus || !this.plus) return;
      const busy = this.closest('cart-items-component')?.classList.contains('cart-items-disabled');
      const disabled = this.select.disabled || busy;
      const choices = this.options(), index = choices.findIndex(o => o.value === this.select.value);
      const remove = this.closest('[ref="cartItemRows[]"]')?.querySelector('.cart-items__remove');
      const canRemove = this.select.value === '1' && remove && !remove.hidden && !remove.disabled;
      this.minus.disabled = !!disabled || (index <= 0 && !canRemove);
      this.minus.setAttribute('aria-label', canRemove ? remove.getAttribute('aria-label') || 'Remove item' : (document.documentElement.lang?.startsWith('ar') ? 'تقليل الكمية' : 'Decrease quantity'));
      this.plus.disabled = !!disabled || index < 0 || index >= choices.length - 1;
      if (this.value.textContent !== this.select.value) this.value.textContent = this.select.value;
    }
    preserveUpdateFocus(direction) {
      const cart = this.closest('cart-items-component');
      const selectId = this.select.id;
      if (!cart || !this.closest('dialog') || !selectId) return;
      const pending = new AbortController();
      let queued = false;
      const observer = new MutationObserver(() => {
        if (queued || cart.classList.contains('cart-items-disabled')) return;
        queued = true;
        // Run after native section morph / modal restoration, including errors.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (pending.signal.aborted) return;
          const wrapper = document.getElementById(selectId)?.closest('sp-cart-quantity');
          if (wrapper?.closest('dialog')?.open) {
            let target = wrapper.querySelector(`[data-step="${direction}"]`);
            if (target?.disabled) target = wrapper.querySelector('.sp-quantity__step:not(:disabled)');
            target?.focus({ preventScroll:true });
          }
          cancel();
        }));
      });
      const cancel = () => { pending.abort(); observer.disconnect(); };
      // Never steal focus after a later deliberate interaction or dialog close.
      document.addEventListener('pointerdown', cancel, { once:true, signal:pending.signal });
      document.addEventListener('keydown', cancel, { once:true, signal:pending.signal });
      observer.observe(cart, { attributes:true, attributeFilter:['class'] });
      setTimeout(cancel, 10000);
    }
    step(direction) {
      this.sync();
      if ((direction < 0 ? this.minus : this.plus).disabled) return;
      if (direction < 0 && this.select.value === '1') {
        const remove = this.closest('[ref="cartItemRows[]"]')?.querySelector('.cart-items__remove');
        if (remove && !remove.hidden && !remove.disabled) remove.click();
        return;
      }
      const choices = this.options(), index = choices.findIndex(o => o.value === this.select.value), next = choices[index + direction];
      if (!next) return;
      this.preserveUpdateFocus(direction);
      this.select.value = next.value;
      this.sync();
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }
    enhance() {
      const select = this.querySelector('select');
      if (!select) return;
      if (this.select === select && this.contains(this.minus) && this.contains(this.plus)) { this.sync(); return; }
      this.querySelectorAll('.sp-quantity__stepper').forEach(n => n.remove());
      this.select = select; select.hidden = true; select.tabIndex = -1;
      this.selectObserver?.disconnect();
      this.selectObserver = new MutationObserver(() => this.sync());
      this.selectObserver.observe(select, { attributes: true, attributeFilter: ['disabled'] });
      const group = document.createElement('div'); group.className = 'sp-quantity__stepper';
      group.setAttribute('role', 'group'); group.setAttribute('aria-label', select.getAttribute('aria-label') || 'Quantity');
      const ar = document.documentElement.lang?.startsWith('ar');
      const makeButton = (direction, label) => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'sp-quantity__step'; button.dataset.step = String(direction);
        button.setAttribute('aria-label', label); button.setAttribute('aria-controls', select.id || '');
        const icon = document.createElement('span'); icon.textContent = direction < 0 ? '−' : '+'; icon.setAttribute('aria-hidden', 'true'); button.append(icon);
        button.addEventListener('click', () => this.step(direction), { signal: this.abort.signal });
        return button;
      };
      this.minus = makeButton(-1, ar ? 'تقليل الكمية' : 'Decrease quantity');
      this.plus = makeButton(1, ar ? 'زيادة الكمية' : 'Increase quantity');
      this.value = document.createElement('span'); this.value.className = 'sp-quantity__value'; this.value.setAttribute('aria-live', 'polite'); this.value.setAttribute('aria-atomic', 'true');
      group.append(this.minus, this.value, this.plus); this.append(group);
      select.addEventListener('change', () => this.sync(), { signal: this.abort.signal }); this.sync();
    }
  });
}
